import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Spinner, Button, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../hooks/AuthProvider";
import { getNotificationsByUser, markNotificationRead, markAllNotificationsRead } from "../../helpers/firestoreService";
import { getNotificationIcon, getNotificationLink, formatTimeAgo } from "../../helpers/notificationDisplay";
import Paginations from "../../components/Common/Pagination";

const PER_PAGE = 10;

/** Shared by both the PM and tenant experiences — notifications are scoped
 * to the logged-in user's uid regardless of role. */
const NotificationsPage = () => {
  document.title = "Notifications | Porcher";

  const user = useAuthUser();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [markingAll, setMarkingAll] = useState(false);

  const load = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const notifs = await getNotificationsByUser(user.uid);
      setNotifications(notifs);
    } catch (e) {
      console.error("Load notifications error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user?.uid]);

  const unreadCount = useMemo(() => notifications.filter((n) => n.readAt == null).length, [notifications]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return notifications.slice(start, start + PER_PAGE);
  }, [notifications, currentPage]);

  const handleClick = async (notif) => {
    if (notif.readAt == null) {
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, readAt: new Date() } : n)));
      markNotificationRead(notif.id).catch((e) => console.error("Mark notification read error:", e));
    }
    const link = getNotificationLink(notif);
    if (link) navigate(link);
  };

  const handleMarkAllRead = async () => {
    if (!user?.uid) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsRead(user.uid);
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date() })));
    } catch (e) {
      console.error("Mark all notifications read error:", e);
    } finally {
      setMarkingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">
            Notifications
            {unreadCount > 0 && (
              <Badge color="danger" pill className="ms-2 align-middle">{unreadCount}</Badge>
            )}
          </h4>
          {unreadCount > 0 && (
            <Button color="link" size="sm" className="text-decoration-none" onClick={handleMarkAllRead} disabled={markingAll}>
              {markingAll ? <Spinner size="sm" /> : "Mark all as read"}
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bx bx-bell-off font-size-40 d-block mb-3" />
            <h5>No notifications yet</h5>
            <p className="small">You'll see updates about tickets, payments, and more here.</p>
          </div>
        ) : (
          <>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-0">
                {pageItems.map((notif) => (
                  <div
                    key={notif.id}
                    role="button"
                    tabIndex={0}
                    className="d-flex align-items-start gap-3 p-3 border-bottom"
                    style={{ cursor: "pointer", background: notif.readAt == null ? "#f6f9ff" : "transparent" }}
                    onClick={() => handleClick(notif)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleClick(notif); }}
                  >
                    <div className="avatar-xs flex-shrink-0">
                      <span className="avatar-title bg-primary rounded-circle font-size-16">
                        <i className={getNotificationIcon(notif.type)} />
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">
                        {notif.title || notif.message}
                        {notif.readAt == null && <span className="ms-2 badge bg-soft-primary text-primary">New</span>}
                      </h6>
                      {notif.title && notif.message && (
                        <p className="text-muted small mb-1">{notif.message}</p>
                      )}
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        <i className="mdi mdi-clock-outline me-1" />
                        {formatTimeAgo(notif.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>

            {notifications.length > PER_PAGE && (
              <div className="mt-3">
                <Paginations
                  data={notifications}
                  perPageData={PER_PAGE}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  isShowingPageLength
                  paginationDiv="col-auto"
                  paginationClass="pagination pagination-rounded mb-0"
                />
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default NotificationsPage;
